import React, { useState } from 'react';
import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Collapse,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { setSnackError } from '../Reducers/snackErrors';
import { AWS_LINK, DEV_LINK } from '../Static/static';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';


// ─── Validation ───────────────────────────────────────────────────────────────
const validationSchema = Yup.object({
  first_name: Yup.string()
    .trim()
    .min(2, 'Too short')
    .max(50, 'Too long')
    .matches(/^[a-zA-Z .'-]+$/, 'Letters only')
    .required('Required'),
  last_name: Yup.string()
    .trim()
    .min(2, 'Too short')
    .max(50, 'Too long')
    .matches(/^[a-zA-Z .'-]+$/, 'Letters only')
    .required('Required'),
  join_code: Yup.string()
    .trim()
    .transform(v => (v ? v.toUpperCase() : v))
    .matches(/^[A-Z0-9]{6,10}$/, '6–10 letters or numbers')
    .required('Required'),
});


// ─── Small "step" indicator ───────────────────────────────────────────────────
function StepDot({ n, active, done }) {
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: '0.75rem',
        fontWeight: 700,
        transition: 'all 0.2s',
        bgcolor: done
          ? 'success.main'
          : active
          ? 'primary.main'
          : 'action.disabledBackground',
        color: done || active ? 'primary.contrastText' : 'text.disabled',
      }}
    >
      {done ? <CheckCircleRoundedIcon sx={{ fontSize: 16 }} /> : n}
    </Box>
  );
}


// ─── Result card ─────────────────────────────────────────────────────────────
function SessionResultCard({ result, onStart }) {
  return (
    <Collapse in={!!result} unmountOnExit>
      <Box
        sx={{
          mt: 1,
          p: 2.5,
          borderRadius: 3,
          border: '1.5px solid',
          borderColor: 'success.light',
          bgcolor: theme =>
            theme.palette.mode === 'dark' ? 'success.900' : 'success.50',
        }}
      >
        {/* Header row */}
        <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: 'success.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CheckCircleRoundedIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" color="success.dark" fontWeight={700}>
              Session found
            </Typography>
            <Typography variant="caption" color="text.secondary">
              You're all set — tap below to begin
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ mb: 2, borderColor: 'success.light' }} />

        {/* Details */}
        <Stack spacing={1.25} mb={2.5}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <EventNoteRoundedIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Assessment
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {result?.title || 'Assessment'}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <BadgeRoundedIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Date &amp; time
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {new Date().toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Button
          fullWidth
          variant="contained"
          color="success"
          size="large"
          endIcon={<PlayArrowRoundedIcon />}
          onClick={onStart}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '1rem',
            py: 1.25,
          }}
        >
          Start assessment
        </Button>
      </Box>
    </Collapse>
  );
}


// ─── Main component ───────────────────────────────────────────────────────────
function StudentSessionSearch() {
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const link = process.env.NODE_ENV === 'production' ? AWS_LINK : DEV_LINK;

  const handleSubmit = async (values, formikHelpers) => {
    setErrorMessage('');
    setResult(null);
    try {
      const payload = {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        join_code: values.join_code.trim().toUpperCase(),
      };
      const response = await axios.get(`${link}/get_assessment_session`, { params: payload });
      if (response.data.data) setResult(response.data.data);
    } catch (error) {
      const msg = error?.response?.data;
      setErrorMessage(typeof msg === 'string' ? msg : 'Unable to find session. Check your details and try again.');
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  const handleStartAssessment = () => {
    if (!result) return;
    navigate(`/assessment/${result.session_token}/${result.student_id}/${result.assessment_id}`);
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 480,
        mx: 'auto',
        my: { xs: 3, sm: 6 },
        px: 2,
      }}
    >
      {/* ── Page header ── */}
      <Stack alignItems="center" spacing={1} mb={4}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 3,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 0.5,
          }}
        >
          <EventNoteRoundedIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Typography variant="h5" fontWeight={800} textAlign="center">
          Find your session
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={320}>
          Enter your name and the join code given by your instructor.
        </Typography>
      </Stack>

      {/* ── Card ── */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          p: { xs: 2.5, sm: 3.5 },
          boxShadow: theme =>
            theme.palette.mode === 'light'
              ? '0 4px 24px rgba(0,0,0,0.07)'
              : 'none',
        }}
      >
        <Formik
          initialValues={{ first_name: '', last_name: '', join_code: '' }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnBlur
          validateOnChange={false}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit: formikSubmit, isSubmitting }) => {
            // Determine which "step" the user is on for subtle UX hints
            const namesDone =
              values.first_name.length >= 2 && values.last_name.length >= 2;
            const codeDone = /^[A-Z0-9]{6,10}$/.test(values.join_code);

            return (
              <Box component="form" noValidate onSubmit={formikSubmit}>
                <Stack spacing={3}>

                  {/* Step 1 – Name */}
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <StepDot n={1} active={!namesDone} done={namesDone} />
                    <Stack spacing={1.5} flex={1}>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        Your name
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                          fullWidth
                          label="First name"
                          name="first_name"
                          value={values.first_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.first_name && Boolean(errors.first_name)}
                          helperText={touched.first_name && errors.first_name}
                          size="small"
                          autoComplete="given-name"
                          autoFocus
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BadgeRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          fullWidth
                          label="Last name"
                          name="last_name"
                          value={values.last_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.last_name && Boolean(errors.last_name)}
                          helperText={touched.last_name && errors.last_name}
                          size="small"
                          autoComplete="family-name"
                        />
                      </Stack>
                    </Stack>
                  </Stack>

                  <Divider />

                  {/* Step 2 – Join code */}
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <StepDot n={2} active={namesDone && !codeDone} done={codeDone} />
                    <Stack spacing={1.5} flex={1}>
                      <Box>
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          Join code
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Given by your instructor — 6 to 10 characters
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        label="e.g. AB12CD"
                        name="join_code"
                        value={values.join_code}
                        onChange={e => {
                          e.target.value = e.target.value.toUpperCase();
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        error={touched.join_code && Boolean(errors.join_code)}
                        helperText={touched.join_code && errors.join_code}
                        size="small"
                        inputProps={{
                          maxLength: 10,
                          style: {
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            fontSize: '1.05rem',
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <TagRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                  </Stack>

                  {/* Error */}
                  <Collapse in={!!errorMessage}>
                    <Alert
                      severity="error"
                      variant="outlined"
                      onClose={() => setErrorMessage('')}
                      sx={{ borderRadius: 2 }}
                    >
                      {errorMessage}
                    </Alert>
                  </Collapse>

                  {/* Submit */}
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={isSubmitting}
                    endIcon={
                      isSubmitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <ArrowForwardIcon />
                      )
                    }
                    sx={{
                      borderRadius: 2.5,
                      py: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '1rem',
                    }}
                  >
                    {isSubmitting ? 'Searching…' : 'Find my session'}
                  </Button>
                </Stack>
              </Box>
            );
          }}
        </Formik>

        {/* Result */}
        <Box mt={result ? 2.5 : 0}>
          <SessionResultCard result={result} onStart={handleStartAssessment} />
        </Box>
      </Box>

      {/* Footer hint */}
      <Typography
        variant="caption"
        color="text.disabled"
        display="block"
        textAlign="center"
        mt={2}
      >
        Having trouble? Ask your instructor for the correct join code.
      </Typography>
    </Box>
  );
}

export default StudentSessionSearch;